import React from 'react';
import { Box, Typography, IconButton, InputBase } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import MenuIcon from '@material-ui/icons/Menu';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import PersonIcon from '@material-ui/icons/Person';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TuneIcon from '@material-ui/icons/Tune';
import CloseIcon from '@material-ui/icons/Close';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

/**
 * Simulates a generic e-commerce site header
 */
const PseudoHeader = () => (
  <Box
    height="60px"
    bgcolor="#ffffff"
    borderBottom="1px solid #e0e0e0"
    display="flex"
    alignItems="center"
    px={3}
    justifyContent="space-between"
    color="#9e9e9e"
  >
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="h6" style={{ fontWeight: 800, letterSpacing: -1, color: '#bdbdbd' }}>
        <IntlMessages id="ui.searchExperienceV2.preview.shells.searchBoxShell.brand" />
      </Typography>
      <Box ml={4} display={{ xs: 'none', md: 'flex' }} gap={3}>
        <Typography variant="body2"><IntlMessages id="ui.searchExperienceV2.preview.shells.searchBoxShell.categories" /></Typography>
        <Typography variant="body2"><IntlMessages id="ui.searchExperienceV2.preview.shells.searchBoxShell.deals" /></Typography>
        <Typography variant="body2"><IntlMessages id="ui.searchExperienceV2.preview.shells.searchBoxShell.new" /></Typography>
      </Box>
    </Box>

    <Box display="flex" alignItems="center" gap={2} flex={1} maxWidth="600px" mx={4}>
      <Box
        flex={1}
        height="36px"
        bgcolor="#f5f5f5"
        borderRadius="4px"
        display="flex"
        alignItems="center"
        px={2}
        color="#bdbdbd"
      >
        <SearchIcon fontSize="small" />
        <Box ml={1} component="span" fontSize="14px"><IntlMessages id="ui.searchExperienceV2.preview.shells.searchBoxShell.searchProducts" /></Box>
      </Box>
    </Box>

    <Box display="flex" alignItems="center" gap={2}>
      <PersonIcon />
      <ShoppingCartIcon />
      <MenuIcon />
    </Box>
  </Box>
);

const PseudoHero = () => (
  <Box height="300px" bgcolor="#eeeeee" display="flex" alignItems="center" justifyContent="center">
    <Typography variant="h3" style={{ opacity: 0.1, fontWeight: 900 }}><IntlMessages id="ui.searchExperienceV2.preview.shells.searchBoxShell.heroBanner" /></Typography>
  </Box>
);

const PseudoContent = () => (
  <Box p={4} display="flex" gap={2} flexWrap="wrap" justifyContent="center">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <Box key={i} width="200px" height="280px" bgcolor="#f5f5f5" borderRadius={2} />
    ))}
  </Box>
);

/**
 * Site Context Shell for SearchBox
 *
 * Desktop:
 * - Renders a background site (PseudoHeader + Hero)
 * - Renders an overlay layer
 * - Renders the Widget Modal (Input + Toggle + Results) inside the overlay
 *
 * Mobile:
 * - Renders the widget in full screen (covering the site completely)
 */
const SearchBoxShell = ({ children, screen = 'desktop' }) => {
  const t = useLocalizedMessage();
  const isMobile = screen === 'mobile';

  // Widget Render Logic
  const renderWidget = () => (
    <Box
      width={isMobile ? '100%' : '100%'}
      maxWidth={isMobile ? 'none' : '800px'}
      height={isMobile ? '100%' : 'auto'}
      maxHeight={isMobile ? '100%' : '100%'} // Use full height available in overlay
      bgcolor="#fff"
      borderRadius={isMobile ? 0 : 4}
      boxShadow={isMobile ? 'none' : '0 4px 20px rgba(0,0,0,0.15)'}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      position="relative"
      zIndex={20}
      mb={isMobile ? 0 : 2}
    >
      {/* WIDGET HEADER: Search Input + Controls */}
      <Box
        p={2}
        borderBottom="1px solid #eee"
        display="flex"
        alignItems="center"
        gap={isMobile ? 1 : 2}
        flexShrink={0}
      >
        {/* Back button on Mobile, Search Icon on Desktop */}
        {isMobile ? (
          <IconButton size="small"><ArrowBackIcon /></IconButton>
        ) : (
          <SearchIcon color="action" />
        )}

        {/* The Input - Read Only Draft Mode */}
        <Box flex={1}>
          <InputBase
            fullWidth
            placeholder={t('ui.searchExperienceV2.preview.searchPlaceholder')}
            style={{ fontSize: 16, cursor: 'default' }}
            readOnly
            disabled
          />
        </Box>

        {/* Right Side Controls */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small">
            {isMobile ? <TuneIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* WIDGET BODY: The Canvas */}
      <Box
        flex={1} // This ensures the content scrolls INSIDE the widget
        overflow="auto"
        bgcolor="#fff"
        position="relative"
        p={0}
      >
        {children}
      </Box>
    </Box>
  );

  if (isMobile) {
    // Mobile Layout: Full Screen Widget
    return (
      <Box height="100%" width="100%" bgcolor="#fff" overflow="hidden">
        {renderWidget()}
      </Box>
    );
  }

  // Desktop Layout: Site Background + Overlay
  return (
    <Box
      height="100%"
      bgcolor="#fafafa"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      position="relative"
      textAlign="left"
    >
      {/* Site Background */}
      <PseudoHeader />

      <Box flex={1} overflow="hidden" position="relative">
        {/* Scrollable Site Content */}
        <Box height="100%" overflow="auto">
          <PseudoHero />
          <PseudoContent />
        </Box>

        {/* Overlay Backdrop - Fixed relative to the shell, covers site content */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(0,0,0,0.4)"
          zIndex={10}
          display="flex"
          justifyContent="center"
          alignItems="flex-start" // Anchor to top
          pt={2} // Spacing from header
                    // Prevent interaction with background
          style={{ pointerEvents: 'auto' }}
        >
          {renderWidget()}
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBoxShell;
